import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LifeBuoy, Send, Trash2, UserRound } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageList } from "@/components/sg/MessageList";

function TicketDialog({ open, onOpenChange }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/support/ticket", { subject, message });
      toast.success("Sent to the ScopeGuard team. We'll reply by email.");
      setSubject(""); setMessage(""); onOpenChange(false);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send to a human</DialogTitle>
          <DialogDescription>Describe the problem. We reply to your account email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject</Label><Input data-testid="ticket-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Client can't open the link" /></div>
          <div><Label>Details</Label><Textarea data-testid="ticket-message-input" value={message} onChange={(e) => setMessage(e.target.value)} className="min-h-[120px]" /></div>
        </div>
        <DialogFooter>
          <Button data-testid="ticket-submit-btn" disabled={busy || subject.length < 3 || message.length < 10} onClick={submit} className="bg-indigo-600 hover:bg-indigo-500 text-white">
            {busy ? "Sending…" : "Send ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SupportChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);

  useEffect(() => {
    api.get("/support/history").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput(""); setSending(true);
    setMessages((m) => [...m, { role: "user", content: text, id: `u-${Date.now()}` }]);
    try {
      const r = await api.post("/support/chat", { message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, id: `a-${Date.now()}` }]);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const clear = async () => { await api.delete("/support/history"); setMessages([]); };

  return (
    <div className="border border-border rounded-xl bg-card h-[560px] flex flex-col" data-testid="support-chat">
      <div className="p-4 border-b border-border flex items-center gap-2">
        <LifeBuoy className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        <div className="font-display font-bold">Support assistant</div>
        <div className="text-xs text-muted-foreground hidden sm:block">· answers instantly, 24/7</div>
        <button onClick={clear} className="ml-auto text-muted-foreground hover:text-foreground transition-colors" data-testid="support-clear-btn" aria-label="Clear chat">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <MessageList messages={messages} sending={sending} testId="support-messages"
        empty="Ask anything about ScopeGuard — e.g. “How do I send a milestone to my client?” or “Why is my milestone overdue?”" />
      <div className="p-4 border-t border-border space-y-2">
        <div className="flex gap-2">
          <Input data-testid="support-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }} placeholder="Type your question…" />
          <Button data-testid="support-send-btn" disabled={sending} onClick={send} className="bg-indigo-600 hover:bg-indigo-500 text-white"><Send className="h-4 w-4" /></Button>
        </div>
        <button data-testid="support-human-btn" onClick={() => setTicketOpen(true)} className="text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
          <UserRound className="h-3 w-3" /> Still stuck? Send to a human
        </button>
      </div>
      <TicketDialog open={ticketOpen} onOpenChange={setTicketOpen} />
    </div>
  );
}
